import { platformStorageSha256Schema } from '@crest/platform-contracts/storage';
import { z } from 'zod';
import { scanningMediaStableKeySchema } from './scanning-media-semantic-geometry.contract.js';
export const scanningMediaScanAssetsCapability = 'scanning-media.scan-assets.v1';
export const scanningMediaScanAssetKindSchema = z.enum([
    'raw-roomplan',
    'usdz-model',
    'detail-mesh',
    'posed-photo',
    'panorama',
    'depth-map',
    'floor-plan-preview',
    'measurement-report',
    'scan-package',
    'thumbnail',
    'other',
]);
export const scanningMediaScanAssetStateSchema = z.enum([
    'registered',
    'active',
    'quarantined',
    'archived',
]);
const contentTypeSchema = z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/);
export const scanningMediaScanAssetDeclarationSchema = z.strictObject({
    fileName: z.string().trim().min(1).max(240).refine((value) => !/[\\/\u0000-\u001f\u007f]/.test(value) && value !== '.' && value !== '..', 'Scan asset file names cannot contain path separators or control characters.'),
    contentType: contentTypeSchema,
    sizeBytes: z.number().int().positive().max(5 * 1_024 * 1_024 * 1_024),
    sha256: platformStorageSha256Schema,
    capturedAt: z.string().datetime({ offset: true }).nullable(),
    sequenceNumber: z.number().int().nonnegative().max(1_000_000_000).nullable(),
});
const boundedNumberSchema = z.number().finite().min(-1_000_000_000).max(1_000_000_000);
export const scanningMediaScanAssetAnchorSchema = z.strictObject({
    floorKey: scanningMediaStableKeySchema.nullable(),
    roomKey: scanningMediaStableKeySchema.nullable(),
    controlPointKey: scanningMediaStableKeySchema.nullable(),
    confidenceBasisPoints: z.number().int().min(0).max(10_000).nullable(),
    transformToCapture: z.array(boundedNumberSchema).length(16).nullable(),
    cameraIntrinsics: z.strictObject({
        focalLengthX: z.number().finite().positive().max(1_000_000),
        focalLengthY: z.number().finite().positive().max(1_000_000),
        principalPointX: boundedNumberSchema,
        principalPointY: boundedNumberSchema,
        imageWidthPixels: z.number().int().positive().max(100_000),
        imageHeightPixels: z.number().int().positive().max(100_000),
    }).nullable(),
}).superRefine((value, context) => {
    if (value.roomKey !== null && value.floorKey === null) {
        context.addIssue({
            code: 'custom',
            path: ['floorKey'],
            message: 'A room-anchored scan asset must retain its floor identity.',
        });
    }
});
export const scanningMediaScanAssetVersionSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    assetId: z.string().uuid(),
    versionNumber: z.number().int().positive(),
    state: scanningMediaScanAssetStateSchema,
    platformObjectId: z.string().uuid().nullable(),
    actualContentType: contentTypeSchema.nullable(),
    actualSizeBytes: z.number().int().positive().nullable(),
    actualSha256: platformStorageSha256Schema.nullable(),
    storageVerifiedAt: z.string().datetime({ offset: true }).nullable(),
    storageAttachedAt: z.string().datetime({ offset: true }).nullable(),
    quarantineCode: z.string().regex(/^[a-z][a-z0-9-]{0,79}$/).nullable(),
    reason: z.string().trim().min(10).max(500),
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const scanningMediaScanAssetSchema = z.strictObject({
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    assetUid: z.string().uuid(),
    captureId: z.string().uuid(),
    semanticVersionId: z.string().uuid(),
    kind: scanningMediaScanAssetKindSchema,
    storagePurpose: z.string().regex(/^scan-asset-[a-z0-9-]+$/),
    anchor: scanningMediaScanAssetAnchorSchema,
    declaration: scanningMediaScanAssetDeclarationSchema,
    state: scanningMediaScanAssetStateSchema,
    revision: z.number().int().positive(),
    currentVersionId: z.string().uuid(),
    currentVersion: scanningMediaScanAssetVersionSchema,
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
const scanningMediaScanAssetCommandBaseSchema = z.strictObject({
    commandId: z.string().uuid(),
    assetId: z.string().uuid(),
    captureId: z.string().uuid(),
    reason: z.string().trim().min(10).max(500),
});
export const scanningMediaRegisterScanAssetCommandSchema = scanningMediaScanAssetCommandBaseSchema.extend({
    kind: z.literal('register'),
    expectedRevision: z.null(),
    assetUid: z.string().uuid(),
    semanticVersionId: z.string().uuid(),
    assetKind: scanningMediaScanAssetKindSchema,
    anchor: scanningMediaScanAssetAnchorSchema,
    declaration: scanningMediaScanAssetDeclarationSchema,
});
export const scanningMediaFinalizeScanAssetCommandSchema = scanningMediaScanAssetCommandBaseSchema.extend({
    kind: z.literal('finalize'),
    expectedRevision: z.number().int().positive(),
    platformObjectId: z.string().uuid(),
});
export const scanningMediaQuarantineScanAssetCommandSchema = scanningMediaScanAssetCommandBaseSchema.extend({
    kind: z.literal('quarantine'),
    expectedRevision: z.number().int().positive(),
    quarantineCode: z.string().regex(/^[a-z][a-z0-9-]{0,79}$/),
});
export const scanningMediaArchiveScanAssetCommandSchema = scanningMediaScanAssetCommandBaseSchema.extend({
    kind: z.literal('archive'),
    expectedRevision: z.number().int().positive(),
});
export const scanningMediaScanAssetCommandSchema = z.discriminatedUnion('kind', [
    scanningMediaRegisterScanAssetCommandSchema,
    scanningMediaFinalizeScanAssetCommandSchema,
    scanningMediaQuarantineScanAssetCommandSchema,
    scanningMediaArchiveScanAssetCommandSchema,
]);
export const scanningMediaScanAssetListQuerySchema = z.strictObject({
    state: scanningMediaScanAssetStateSchema.optional(),
    assetKind: scanningMediaScanAssetKindSchema.optional(),
    includeArchived: z.boolean().default(false),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(100_000).default(0),
});
export const scanningMediaScanAssetResponseSchema = z.strictObject({
    data: scanningMediaScanAssetSchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
export const scanningMediaScanAssetsResponseSchema = z.strictObject({
    data: z.array(scanningMediaScanAssetSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const scanningMediaScanAssetVersionsResponseSchema = z.strictObject({
    data: z.array(scanningMediaScanAssetVersionSchema),
});
const allowedContentTypes = {
    'raw-roomplan': ['application/json', 'application/x-plist', 'application/octet-stream'],
    'usdz-model': ['model/vnd.usdz+zip', 'model/vnd.usd', 'application/octet-stream'],
    'detail-mesh': ['model/gltf-binary', 'model/gltf+json', 'application/octet-stream'],
    'posed-photo': ['image/jpeg', 'image/png', 'image/heic'],
    panorama: ['image/jpeg', 'image/png', 'image/heic'],
    'depth-map': ['image/tiff', 'image/png', 'application/octet-stream'],
    'floor-plan-preview': ['image/jpeg', 'image/png', 'image/heic'],
    'measurement-report': ['application/pdf', 'application/json'],
    'scan-package': ['application/zip', 'application/octet-stream'],
    thumbnail: ['image/jpeg', 'image/png', 'image/heic'],
    other: [
        'application/octet-stream', 'application/json', 'application/pdf', 'application/zip',
        'image/jpeg', 'image/png', 'image/heic', 'image/tiff', 'video/mp4',
        'model/gltf-binary', 'model/gltf+json', 'model/vnd.usdz+zip',
    ],
};
export function scanningMediaScanAssetContentTypeAllowed(kind, contentType) {
    return allowedContentTypes[kind].includes(contentType.trim().toLowerCase());
}
export function scanningMediaScanAssetStoragePurpose(kind) {
    return `scan-asset-${kind}`;
}
//# sourceMappingURL=scanning-media-scan-asset.contract.js.map